import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";

const LEVELS = [["required", "Required"], ["preferred", "Preferred"], ["na", "Not applicable"]];

export default function ClientRequirements() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("training_manager", "administrator");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [rows, setRows] = useState([]);
  const [ctypes, setCtypes] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ target: "type", competency_type_id: "", training_category_id: "", level: "required", min_validity_days: "", must_remain_valid: false, client_wording: "" });

  useEffect(() => {
    Promise.all([
      api.get("/config/clients").catch(() => ({ data: [] })),
      api.get("/config/competency-types").catch(() => ({ data: [] })),
      api.get("/training/categories").catch(() => ({ data: [] })),
    ]).then(([cl, ct, ca]) => {
      setClients((cl.data || []).filter((c) => c.is_active !== false));
      setCtypes((ct.data || []).filter((t) => t.is_active !== false));
      setCats(ca.data || []);
    });
  }, []);

  const load = useCallback(() => {
    if (!clientId) { setRows([]); return; }
    api.get(`/training/client-requirements?client_id=${clientId}`).then((r) => setRows(r.data)).catch(() => {});
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const payload = {
      client_id: clientId, level: form.level,
      min_validity_days: form.min_validity_days ? parseInt(form.min_validity_days, 10) : null,
      must_remain_valid: form.must_remain_valid, client_wording: form.client_wording || null,
    };
    if (form.target === "type") payload.competency_type_id = form.competency_type_id;
    else payload.training_category_id = form.training_category_id;
    if (!payload.competency_type_id && !payload.training_category_id) { toast.error("Choose a competency or category"); return; }
    try {
      await api.post("/training/client-requirements", payload);
      toast.success("Requirement added");
      setForm({ target: "type", competency_type_id: "", training_category_id: "", level: "required", min_validity_days: "", must_remain_valid: false, client_wording: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setLevel = async (r, level) => {
    setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, level } : x)));
    try { await api.put(`/training/client-requirements/${r.id}`, { level }); await load(); }
    catch (e) { toast.error("Failed"); load(); }
  };
  const remove = async (r) => {
    if (!window.confirm("Remove this requirement?")) return;
    try { await api.delete(`/training/client-requirements/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="client-requirements-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="req-client-select" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Choose a client…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {!clientId && <p className="text-slate-400 text-sm">Select a client to manage their training requirements.</p>}

      {clientId && (
        <>
          {canManage && (
            <div className="bg-white border-2 border-slate-200 rounded-lg p-4 flex flex-col gap-3" data-testid="req-add-form">
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex rounded-lg border-2 border-slate-200 overflow-hidden">
                  <button data-testid="req-target-type" onClick={() => setForm({ ...form, target: "type" })} className={`px-3 h-9 text-sm font-semibold ${form.target === "type" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>Competency</button>
                  <button data-testid="req-target-cat" onClick={() => setForm({ ...form, target: "category" })} className={`px-3 h-9 text-sm font-semibold ${form.target === "category" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>Category</button>
                </div>
                {form.target === "type" ? (
                  <select data-testid="req-type" className="h-9 border-2 border-slate-300 rounded px-2 text-sm min-w-[200px]" value={form.competency_type_id} onChange={(e) => setForm({ ...form, competency_type_id: e.target.value })}>
                    <option value="">Choose competency…</option>{ctypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                ) : (
                  <select data-testid="req-cat" className="h-9 border-2 border-slate-300 rounded px-2 text-sm min-w-[200px]" value={form.training_category_id} onChange={(e) => setForm({ ...form, training_category_id: e.target.value })}>
                    <option value="">Choose category…</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
                <select data-testid="req-level" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
                  {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Input data-testid="req-minvalidity" type="number" placeholder="Min days valid" value={form.min_validity_days} onChange={(e) => setForm({ ...form, min_validity_days: e.target.value })} className="h-9 w-36" />
                <label className="flex items-center gap-1.5 text-sm text-slate-600"><input type="checkbox" data-testid="req-mustremain" checked={form.must_remain_valid} onChange={(e) => setForm({ ...form, must_remain_valid: e.target.checked })} />Must remain valid for job</label>
              </div>
              <div className="flex gap-2">
                <Input data-testid="req-wording" placeholder="Client-specific wording (optional)" value={form.client_wording} onChange={(e) => setForm({ ...form, client_wording: e.target.value })} className="h-9 flex-1" />
                <Button data-testid="req-add" onClick={add} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold"><Plus size={16} className="mr-1" />Add</Button>
              </div>
            </div>
          )}

          <div className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden">
            {rows.length === 0 ? <p className="text-slate-400 text-sm p-4">No requirements set for this client yet.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-100 text-left text-xs uppercase text-slate-500">
                  <th className="px-3 py-2">Requirement</th><th className="px-3 py-2">Level</th><th className="px-3 py-2">Rules</th><th className="px-3 py-2"></th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`req-row-${r.id}`} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold text-slate-800">
                        {r.competency_name || (r.category_name ? `${r.category_name} (category)` : "—")}
                      </td>
                      <td className="px-3 py-2">
                        <select data-testid={`req-row-level-${r.id}`} disabled={!canManage} className="h-8 border-2 border-slate-200 rounded px-1 text-xs" value={r.level} onChange={(e) => setLevel(r, e.target.value)}>
                          {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {r.min_validity_days ? `≥ ${r.min_validity_days} days` : ""}{r.must_remain_valid ? (r.min_validity_days ? " · " : "") + "valid for job" : ""}
                        {r.client_wording ? ` · ${r.client_wording}` : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canManage && <Button data-testid={`req-del-${r.id}`} onClick={() => remove(r)} variant="outline" className="h-8 px-2 text-red-600"><Trash size={14} /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
